import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { NotificationsService } from "angular2-notifications";
import { ClientDocumentService } from "../../services/client-document.service";
import { IClientDocument } from "../../model/client-document.model";

@Component({
  selector: "app-client-documents",
  templateUrl: "./client-documents.component.html",
  styleUrls: ["./client-documents.component.scss"]
})
export class ClientDocumentsComponent implements OnInit {
  @Input() clientId: string | null = null;
  @ViewChild("deleteConfirmModal") deleteConfirmModal!: TemplateRef<any>;

  loading = false;
  error: string | null = null;
  documents: IClientDocument[] = [];
  selectedFile: File | null = null;
  uploadTags = "";
  uploadDocumentType = "";
  uploadNotes = "";
  uploading = false;
  uploadError: string | null = null;
  searchTerm = "";
  tagFilter = "";
  deletingDocumentId: string | null = null;
  pendingDeleteDocument: IClientDocument | null = null;
  deleteError: string | null = null;
  modalRef?: NgbModalRef;

  constructor(
    private clientDocumentService: ClientDocumentService,
    private modalService: NgbModal,
    private notifications: NotificationsService
  ) {}

  ngOnInit(): void {
    if (!this.clientId) {
      console.warn("ClientDocumentsComponent: missing clientId input");
      return;
    }

    this.loadDocuments();
  }

  loadDocuments(): void {
    if (!this.clientId) {
      return;
    }

    this.loading = true;
    this.error = null;

    const search =
      this.searchTerm && this.searchTerm.trim().length
        ? this.searchTerm.trim()
        : undefined;

    const tag =
      this.tagFilter && this.tagFilter.trim().length
        ? this.tagFilter.trim()
        : undefined;

    this.clientDocumentService
      .clientDocumentListing({
        clientId: this.clientId,
        search,
        tag
      })
      .subscribe({
        next: (res) => {
          const anyRes: any = res;
          const data: IClientDocument[] = Array.isArray(anyRes)
            ? anyRes
            : anyRes.data || [];

          this.documents = data || [];
          this.loading = false;
        },
        error: (err) => {
          console.error("Failed to load client documents", err);
          this.error = "Failed to load documents.";
          this.loading = false;
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.selectedFile = null;
      return;
    }
    this.selectedFile = input.files[0];
    this.uploadError = null;
  }

  upload(): void {
    if (!this.clientId) {
      this.uploadError = "Missing client ID.";
      return;
    }

    if (!this.selectedFile) {
      this.uploadError = "Please choose a file to upload.";
      return;
    }

    this.uploading = true;
    this.uploadError = null;

    const formData = new FormData();
    formData.append("file", this.selectedFile);
    formData.append("clientId", this.clientId);

    if (this.uploadTags && this.uploadTags.trim().length) {
      formData.append("tags", this.uploadTags.trim());
    }

    if (this.uploadDocumentType && this.uploadDocumentType.trim().length) {
      formData.append("documentType", this.uploadDocumentType.trim());
    }

    if (this.uploadNotes && this.uploadNotes.trim().length) {
      formData.append("notes", this.uploadNotes.trim());
    }

    this.clientDocumentService.upload(formData).subscribe({
      next: (res) => {
        // Support both envelope and bare document
        const anyRes: any = res;
        const doc = anyRes.data || anyRes;
        console.log("Upload successful", doc);

        this.selectedFile = null;
        this.uploadTags = "";
        this.uploadDocumentType = "";
        this.uploadNotes = "";
        this.uploading = false;
        this.uploadError = null;

        this.loadDocuments();
      },
      error: (err) => {
        console.error("Failed to upload document", err);
        this.uploadError = "Failed to upload document.";
        this.uploading = false;
      }
    });
  }

  /**
   * Normalise tags coming back as string | string[] | null.
   * Backend currently sends tags as a JSON string: "[\"consent_form\",\"referral\"]"
   */
  normalizeTags(doc: IClientDocument): string[] {
    const raw = doc.tags;

    if (!raw) {
      return [];
    }

    if (Array.isArray(raw)) {
      return raw.filter((t) => !!t);
    }

    const str = raw.trim();

    if (!str.length) {
      return [];
    }

    if (str.startsWith("[")) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) {
          return parsed.map((t) => String(t).trim()).filter(Boolean);
        }
      } catch {
        // fall back to comma split
      }
    }

    return str
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  download(doc: IClientDocument): void {
    this.clientDocumentService.getDownloadUrl(doc.id).subscribe({
      next: (res) => {
        const anyRes: any = res;
        const url: string =
          anyRes.url ||
          (anyRes.data && anyRes.data.url) ||
          "";

        if (!url) {
          console.error("Download URL missing in response", res);
          return;
        }

        window.open(url, "_blank");
      },
      error: (err) => {
        console.error("Failed to get download URL", err);
        this.error = "Failed to get download URL.";
      }
    });
  }

  openDeleteConfirm(doc: IClientDocument): void {
    this.pendingDeleteDocument = doc;
    this.deleteError = null;
    this.modalRef = this.modalService.open(this.deleteConfirmModal, { centered: true });
  }

  cancelDelete(): void {
    if (this.deletingDocumentId) {
      return;
    }

    this.deleteError = null;
    this.pendingDeleteDocument = null;
    this.modalRef?.dismiss();
    this.modalRef = undefined;
  }

  confirmDelete(): void {
    const doc = this.pendingDeleteDocument;

    if (!doc || this.deletingDocumentId) {
      return;
    }

    this.deletingDocumentId = doc.id;
    this.deleteError = null;

    this.clientDocumentService.deleteDocument(doc.id).subscribe({
      next: (res) => {
        const anyRes: any = res;
        const status = anyRes?.status;
        const success = anyRes?.success;

        if (status === false || success === false) {
          const message = anyRes?.message || "Failed to delete document.";
          this.deleteError = message;
          this.notifications.error("ERROR", message, {});
          this.deletingDocumentId = null;
          return;
        }

        this.deletingDocumentId = null;
        this.deleteError = null;
        this.pendingDeleteDocument = null;
        this.modalRef?.close();
        this.modalRef = undefined;
        this.loadDocuments();
      },
      error: (err) => {
        console.error("Failed to delete document", err);
        const message =
          (err && err.error && err.error.message) ||
          err?.message ||
          "Failed to delete document.";

        this.deleteError = message;
        this.notifications.error("ERROR", message, {});
        this.deletingDocumentId = null;
      }
    });
  }
}
